import CustomerSelect from '@/components/Form/Shared/CustomerSelect'
import CustomerServiceSelect from '@/components/Form/Shared/CustomerServiceSelect'
import { useModal } from '@/contexts/modal'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import useUploadFile from '@/hooks/useUploadFile'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getSupplierOptions } from '@/modules/Contract/modules/Partner/services'
import type { Supplier } from '@/modules/Contract/modules/Partner/types'
import {
    CONTRACT_DIRECTION_OPTIONS,
    CONTRACT_ROLE_OPTIONS,
    CONTRACT_STATUS,
} from '@/modules/Contract/constants'
import { STORGE_PREFIXS } from '@/modules/Contract/modules/Process/constants'
import { updateContractCustomer } from '@/modules/Contract/services/customer'
import {
    CustomerContract,
    UpdateCustomerContractParams,
} from '@/modules/Contract/types'
import { Attachment } from '@/modules/Upload/types'
import { ModalProps } from '@/types/shared'
import { formatPGDate, renderFileItem } from '@/utils/function'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, GridHookForm, Typogaphy } from 'vnetwork-ui'
import { schemaUpdateContractProcessModal } from './schema'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEY_COMBINATIONS, QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Plus } from 'lucide-react'

export interface UpdateContractProcessFormValues {
    contract: CustomerContract
    attachments?: File[]
}

export interface IUpdateContractProps extends ModalProps {
    contractID: number
    contract: CustomerContract
}

const CreateContractProcessModal = ({
    onClose,
    open,
    contractID,
    contract,
}: IUpdateContractProps) => {
    const { t } = useTranslation('contract')
    const { setModal } = useModal()
    const { refetch } = useQueryClientCallback({
        pattern: 'contracts',
        mode: 'contains',
    })
    const supplierOptionsQuery = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.SUPPLIERS, 'options'],
            queryFn: getSupplierOptions,
        },
        { showError: false, showLoading: false }
    )

    const formControl = useForm<{
        contract: UpdateCustomerContractParams
        service_ids: number[]
        attachments?: File[] | Attachment[]
    }>({
        defaultValues: {
            contract: {
                contract_number_id: contract?.contract_number_id,
                customer_id: contract?.customer_id,
                contract_value: contract?.contract_value,
                contract_status: contract?.contract_status,
                contract_key: contract?.contract_key,
                contract_effective_date: contract?.contract_effective_date,
                contract_expiry_date: contract?.contract_expiry_date,
                contract_role: contract?.contract_role ?? 'customer',
                contract_direction: contract?.contract_direction ?? 'earn',
                sla_enable: contract?.sla_enable,
                nda_enable: contract?.nda_enable,
                partner_id: contract?.partner_id ?? null,
            },
            service_ids:
                contract?.services?.map((service) => service?.service_id) || [],
            attachments: contract?.attachments,
        },
        resolver: yupResolver(schemaUpdateContractProcessModal as any),
    })

    const supplierOptions =
        supplierOptionsQuery.data?.map((supplier) => ({
            label: supplier.partner_code
                ? `${supplier.partner_name} (${supplier.partner_code})`
                : supplier.partner_name,
            value: supplier.partner_id,
        })) || []

    const openSupplierModal = () => {
        setModal('ContractModals/SupplierModal/index', {
            mode: 'create',
            onCreated: (supplier: Supplier) => {
                supplierOptionsQuery.refetch()
                formControl.setValue(
                    'contract.partner_id',
                    supplier.partner_id,
                    {
                        shouldDirty: true,
                        shouldValidate: true,
                    }
                )
            },
        })
    }

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: updateContractCustomer,
        },
        {
            successMessage: t('update-contract-customer-success'),
            reactQueryKeys: QUERY_KEY_COMBINATIONS.CONTRACT_WITH_SERVICE,
            onSuccess: () => {
                onClose()
                refetch()
            },
        }
    )

    const { isUploading, uploadAsync } = useUploadFile({
        prefix: STORGE_PREFIXS.CONTRACT_PROCESS,
    })
    const onSubmit = async (values: {
        contract: UpdateCustomerContractParams
        attachments?: File[] | Attachment[]
        service_ids: number[]
    }) => {
        let attachments: Attachment[] = []
        let attachmentsToDeleteIds: number[] = []
        if (values?.attachments) {
            const attachmentsToUpload = values?.attachments?.filter(
                (attachment) => attachment instanceof File
            ) as File[]
            const attachmentUploadeds = await uploadAsync(attachmentsToUpload)

            attachments = attachmentUploadeds
                ?.filter((attachment) => attachment.status === 'fulfilled')
                .map((attachment) => attachment.value) as Attachment[]

            const attachmentsToKeep = values?.attachments?.filter(
                (attachment) => !(attachment instanceof File)
            ) as Attachment[]
            attachments = [...attachments]
            // get attachments to delete
            const keepIDs = attachmentsToKeep.map(
                (attachment) => attachment?.attachment_id
            )

            attachmentsToDeleteIds = contract?.attachments
                ?.filter(
                    (attachment) => !keepIDs.includes(attachment?.attachment_id)
                )
                .map((attachment) => attachment?.attachment_id)
        }

        mutateAsync({
            contractID,
            data: {
                ...values?.contract,
                contract_expiry_date: formatPGDate(
                    values?.contract?.contract_expiry_date as string,
                    'YYYY-MM-DD'
                ),
                contract_effective_date: formatPGDate(
                    values?.contract?.contract_effective_date as string,
                    'YYYY-MM-DD'
                ),
            },
            attachments,
            attachmentsToDelete: attachmentsToDeleteIds,
            service_ids: values?.service_ids || [],
        })
    }

    const onDelete = (file: File | Attachment) => {
        const isFile = file instanceof File
        const isAttachment = 'attachment_file_name' in file

        if (isFile) {
            const attachments = formControl.getValues('attachments')
            const newAttachments = attachments?.filter((attachment) => {
                if (attachment instanceof File) {
                    return attachment.name !== file.name
                }
                return true
            }) as Attachment[] | File[]
            formControl.setValue('attachments', newAttachments)
            return
        }

        if (isAttachment) {
            const attachments = formControl.getValues('attachments')
            const newAttachments = attachments?.filter((attachment) => {
                if (attachment instanceof File) {
                    return true
                }
                return (
                    attachment.attachment_file_name !==
                    (file as Attachment).attachment_file_name
                )
            }) as Attachment[] | File[]
            formControl.setValue('attachments', newAttachments)
        }
    }

    return (
        <Dialog title={t('update-contract')} onClose={onClose} open={open}>
            <GridHookForm
                className="!gap-4"
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isUploading || isPending,
                    },
                    {
                        title: t('submit'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isUploading || isPending,
                        disabled: isUploading || isPending,
                    },
                ]}
                onSubmit={onSubmit}
                data={[
                    {
                        formItemProps: {
                            name: 'contract.contract_number_id',
                            type: 'textfield',
                            disabled: true,
                            helperText: t(
                                'use-can-not-edit-contract-number-id'
                            ),
                            label: t('contract_number_id'),
                            placeholder: t('contract_number_id'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        className: 'col-span-4',
                        renderEditComponent(control) {
                            return (
                                <CustomerSelect
                                    label="contract_owner"
                                    control={control}
                                    name={'contract.customer_id'}
                                    showLabel
                                    onChangeAfterHookForm={() => {
                                        formControl.setValue('service_ids', [])
                                    }}
                                />
                            )
                        },
                    },
                    {
                        className: 'col-span-4',
                        renderEditComponent(control) {
                            return (
                                <>
                                    <CustomerServiceSelect
                                        disabled={
                                            !formControl.watch(
                                                `contract.customer_id`
                                            )
                                        }
                                        multiple
                                        customerID={formControl.watch(
                                            `contract.customer_id`
                                        )}
                                        payload={{
                                            customer_id:
                                                formControl.watch(
                                                    `contract.customer_id`
                                                ),
                                        }}
                                        label="contract_service"
                                        control={control}
                                        name={'service_ids'}
                                        showLabel
                                    />
                                    {formControl?.formState?.errors
                                        ?.service_ids && (
                                        <Typogaphy className="text-red-500 mt-1">
                                            {
                                                formControl?.formState?.errors
                                                    ?.service_ids?.message
                                            }
                                        </Typogaphy>
                                    )}
                                </>
                            )
                        },
                    },
                    {
                        formItemProps: {
                            name: 'contract.partner_id',
                            type: 'select',
                            label: t('partner_name'),
                            options: supplierOptions,
                            allowClear: true,
                        },
                        className: 'col-span-4',
                    },
                    {
                        renderEditComponent: () => (
                            <Button
                                type="button"
                                variant="outlined"
                                size="small"
                                startIcon={<Plus className="h-4 w-4" />}
                                onClick={openSupplierModal}
                                className="w-full"
                            >
                                {t('create-supplier')}
                            </Button>
                        ),
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'contract.contract_key',
                            type: 'textfield',
                            label: t('contract_key'),
                            placeholder: 'contract_key',
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'contract.sla_enable',
                            type: 'checkbox',
                            label: t('sla_enable'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contract.nda_enable',
                            type: 'checkbox',
                            label: t('nda_enable'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contract.contract_effective_date',
                            type: 'datepicker',
                            label: t('contract_effective_date'),
                            placeholder: 'contract_effective_date',
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contract.contract_expiry_date',
                            type: 'datepicker',
                            label: t('contract_expiry_date'),
                            placeholder: 'contract_expiry_date',
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contract.contract_value',
                            type: 'number',
                            label: t('contract_value'),
                            placeholder: 'contract_value',
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contract.contract_status',
                            type: 'select',
                            label: t('contract_status'),
                            placeholder: 'contract_status',
                            options: CONTRACT_STATUS(t),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contract.contract_role',
                            type: 'select',
                            label: t('contract_role'),
                            placeholder: t('contract_role'),
                            options: CONTRACT_ROLE_OPTIONS(t),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contract.contract_direction',
                            type: 'select',
                            label: t('contract_direction'),
                            placeholder: t('contract_direction'),
                            options: CONTRACT_DIRECTION_OPTIONS(t),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'attachments',
                            type: 'files',
                            multiple: true,
                            renderFileItem: (file) =>
                                renderFileItem(file, onDelete),
                            label: t('attachments'),

                            placeholder: 'attachments',
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default CreateContractProcessModal
